import Calendar16 from '@carbon/icons-react/es/calendar/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { TimesheetEntryType } from 'api/timesheets';
import {
  TimesheetReportsSummary,
  TimesheetTaskReport,
} from 'api/timesheets/reports/types';
import { ReactComponent as Activity24Icon } from 'assets/svgs/activity.svg';
import { formatter } from 'components/atoms/input-duration';
import { StatusIndicatorIcon } from 'components/molecules/status-indicator';
import { NameCellWithTooltip } from 'components/organisms/timesheets/common/cells/name-cell';
import { PercentWithTimeCell } from 'components/organisms/timesheets/common/cells/percent-with-time-cell';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { calculatePercentage } from 'components/organisms/timesheets/common/utils';
import { StyledNameCell } from './project-wise-phase-config';
import {
  billableMinutesSorter,
  nonBillableMinutesSorter,
  taskNameSorter,
  totalMinutesSorter,
} from './sorter';

export const useProjectWiseTaskConfig = ({
  noOfRows,
}: {
  noOfRows: number;
}) => {
  return [
    {
      title: <FormattedMessage id="tasks" values={{ count: noOfRows }} />,
      key: 'task',
      width: '310px',
      render: (_, record: TimesheetTaskReport) => {
        const { task } = record;

        if (
          ![
            TimesheetEntryType.TaskEntry,
            TimesheetEntryType.AdhocEntry,
            TimesheetEntryType.Integration,
          ].includes(task?.entryType)
        ) {
          return (
            <StyledNameCell
              logo={<ListChecked16 />}
              primaryText={<FormattedMessage id="timesheets.column.no_task" />}
            />
          );
        }

        if (task.entryType === TimesheetEntryType.TaskEntry) {
          return (
            <NameCellWithTooltip
              logo={
                <StatusIndicatorIcon
                  group={task.status}
                  type={task.type}
                  progress={task.progress}
                />
              }
              primaryText={task.name}
            />
          );
        }

        return (
          <StyledNameCell
            logo={
              task.entryType === TimesheetEntryType.Integration ? (
                <Calendar16 />
              ) : (
                <Activity24Icon />
              )
            }
            primaryText={task.activityName}
          />
        );
      },
      summaryRender: () => <FormattedMessage id="timesheets.column.total" />,
      defaultSortOrder: 'ascend',
      sorter: taskNameSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.effort" />,
      key: 'effort',
      width: '200px',
      render: (_, record: TimesheetTaskReport) => {
        const { task } = record;

        if (
          task &&
          task.entryType === TimesheetEntryType.TaskEntry &&
          task.effort
        ) {
          return <BaseCell>{formatter(task.effort)}</BaseCell>;
        }

        return <BaseCell>-</BaseCell>;
      },
      summaryRender: (_, summaryRecord: TimesheetReportsSummary) =>
        summaryRecord.effort && summaryRecord.effort !== 0 ? (
          <>{formatter(summaryRecord.effort)}</>
        ) : (
          <>-</>
        ),
      sorter: (
        { task: taskA }: TimesheetTaskReport,
        { task: taskB }: TimesheetTaskReport,
      ) => {
        const getEffort = (task) =>
          task && task.entryType === TimesheetEntryType.TaskEntry
            ? task.effort ?? 0
            : 0;

        return getEffort(taskA) - getEffort(taskB);
      },
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.hours_tracked" />,
      key: 'hoursTracked',
      width: '200px',
      render: (_, record: TimesheetTaskReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        return (
          <BaseCell>
            <FormatTime value={billableMinutes + nonBillableMinutes} />
          </BaseCell>
        );
      },
      summaryRender: (_, summaryRecord: TimesheetTaskReport) => (
        <FormatTime
          value={
            summaryRecord.billableMinutes + summaryRecord.nonBillableMinutes
          }
        />
      ),
      sorter: totalMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.billable_hours" />,
      key: 'billableHours',
      width: '200px',
      render: (_, record: TimesheetTaskReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          billableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={billableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetTaskReport) => (
        <FormatTime value={summaryRecord.billableMinutes} />
      ),
      sorter: billableMinutesSorter,
      showSorterTooltip: false,
    },
    {
      title: <FormattedMessage id="timesheets.column.non_billable_hours" />,
      key: 'nonBillableHours',
      render: (_, record: TimesheetTaskReport) => {
        const { billableMinutes, nonBillableMinutes } = record;

        const percentage = calculatePercentage(
          nonBillableMinutes,
          billableMinutes,
          nonBillableMinutes,
        );

        return (
          <PercentWithTimeCell
            minutes={nonBillableMinutes}
            percentage={percentage}
          />
        );
      },
      summaryRender: (_, summaryRecord: TimesheetTaskReport) => (
        <FormatTime value={summaryRecord.nonBillableMinutes} />
      ),
      sorter: nonBillableMinutesSorter,
      showSorterTooltip: false,
    },
  ];
};
